import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AnalyticsEvents } from '../models/analytics';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private initialized = false;

  init(): void {
    if (
      !environment.analytics.enabled ||
      !environment.analytics.measurementId ||
      this.initialized
    ) {
      return;
    }

    this.loadScript(environment.analytics.measurementId);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => this.trackPage(event.urlAfterRedirects));

    this.initialized = true;
  }

  setUser(userId: string): void {
    if (!environment.analytics.enabled || !environment.analytics.measurementId) {
      return;
    }
    // Set GA user_id (special field)
    window.gtag('config', environment.analytics.measurementId!, {
      user_id: userId,
    });
  }

  clearUser() {
    if (!environment.analytics.enabled || !environment.analytics.measurementId) {
      return;
    }

    window.gtag('config', environment.analytics.measurementId!, {
      user_id: null,
    });
  }

  trackPage(path: string): void {
    if (!environment.analytics.enabled || !this.initialized) return;

    window.gtag('event', AnalyticsEvents.PageTracking, {
      page_path: path,
    });
  }

  trackEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!environment.analytics.enabled || !this.initialized) return;

    window.gtag('event', eventName, params);
  }

  private loadScript(measurementId: string): void {
    window.dataLayer = window.dataLayer || [];

    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

    document.head.appendChild(script);
  }
}
